"use client";

import { useCallback, useMemo, useRef, useState } from "react";

import { captureEvent } from "@/app/_analytics/analytics";
import { useLocalStorageTTL } from "@/app/_ui/hooks/useLocalStorageTTL";
import type { EventName } from "@mw/shared/analyticsEventNames";

const DEFAULT_PLAYBACK_SECONDS_BEFORE_NUDGE = 2;

interface UsePlaybackNudgeOptions {
  impressionEventName: EventName;
  /** Whether the nudge is enabled. Defaults to true. */
  enabled?: boolean;
  /** Seconds of accumulated right-disc playback before the nudge can appear. Default: 2 */
  playbackSecondsThreshold?: number;
  /** Minimum number of unique samples played on the right disc before the nudge can appear. Default: 0 (disabled) */
  minUniqueSamples?: number;
  /** localStorage key for time-bound suppression. Pass `null` to disable suppression. */
  suppressionKey: string | null;
  /** How many days to suppress the nudge after it has been shown. Default: 30 */
  suppressionDays?: number;
}

export function usePlaybackNudge(
  lastPlayedDiscRef: React.RefObject<"left" | "right" | null>,
  options: UsePlaybackNudgeOptions,
) {
  const showNudge = options.enabled ?? true;
  const playbackSecondsThreshold = options.playbackSecondsThreshold ?? DEFAULT_PLAYBACK_SECONDS_BEFORE_NUDGE;
  const minUniqueSamples = options.minUniqueSamples ?? 0;
  const suppressionDays = options.suppressionDays ?? 30;

  const [hasReachedThreshold, setHasReachedThreshold] = useState(false);
  const [isSuppressed, markSuppressed] = useLocalStorageTTL(options.suppressionKey, suppressionDays);
  const nudgeShownRef = useRef(false);
  const accumulatedSecondsRef = useRef(0);
  const lastTimeRef = useRef(0);
  const uniqueSamplesRef = useRef(new Set<string>());

  const registerSamplePlay = useCallback((sampleId: string) => {
    uniqueSamplesRef.current.add(sampleId);
  }, []);

  const resetTrackTime = useCallback((startTime: number) => {
    lastTimeRef.current = startTime;
  }, []);

  const onTimeUpdate = useCallback(
    (currentTime: number) => {
      if (lastPlayedDiscRef.current !== "right") return;

      const delta = currentTime > lastTimeRef.current ? currentTime - lastTimeRef.current : 0;
      if (delta > 0) {
        accumulatedSecondsRef.current += delta;
        if (
          accumulatedSecondsRef.current >= playbackSecondsThreshold &&
          uniqueSamplesRef.current.size >= minUniqueSamples
        ) {
          setHasReachedThreshold(true);
        }
      }
      lastTimeRef.current = currentTime;
    },
    [lastPlayedDiscRef, playbackSecondsThreshold, minUniqueSamples],
  );

  const shouldShow = useCallback(
    (activeTab: string, limitReachedVisible: boolean) =>
      showNudge && hasReachedThreshold && !isSuppressed && activeTab === "samples" && !limitReachedVisible,
    [showNudge, hasReachedThreshold, isSuppressed],
  );

  const trackImpression = useCallback(() => {
    if (!nudgeShownRef.current) {
      captureEvent(options.impressionEventName);
      nudgeShownRef.current = true;
      markSuppressed();
    }
  }, [options.impressionEventName, markSuppressed]);

  return useMemo(
    () => ({ shouldShow, onTimeUpdate, resetTrackTime, registerSamplePlay, trackImpression }),
    [shouldShow, onTimeUpdate, resetTrackTime, registerSamplePlay, trackImpression],
  );
}
